import { useEffect, useState, useRef } from "react";
import { api, mediaUrl } from "../../lib/api";
import { Folder, FolderPlus, Upload, Trash2, Copy, Loader2, Image as ImageIcon, Check, Star, AlertTriangle } from "lucide-react";

export default function AdminMedia() {
  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null); // null = all
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [pendingUpload, setPendingUpload] = useState(null); // { files, folder }
  const [pendingAlt, setPendingAlt] = useState("");
  const [editingAlt, setEditingAlt] = useState(null); // media_id
  const [altDraft, setAltDraft] = useState("");
  const inputRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    const params = active ? { folder: active } : {};
    const [foldersRes, mediaRes] = await Promise.all([
      api.get("/admin/media/folders"),
      api.get("/admin/media", { params }),
    ]);
    setFolders(foldersRes.data.folders);
    setItems(mediaRes.data.media);
    setLoading(false);
  };

  useEffect(() => { reload(); }, [active]);

  const onFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Show alt-text prompt for image uploads so admins can never accidentally
    // publish an unlabelled photo. Non-image uploads (PDFs, videos) skip it.
    const isImageBatch = files.every((f) => (f.type || "").startsWith("image/"));
    const folder = active || newFolderName.trim() || "uploads";
    if (isImageBatch) {
      setPendingUpload({ files, folder });
      setPendingAlt("");
    } else {
      runUpload(files, folder, "");
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const runUpload = async (files, folder, altText) => {
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", folder);
        if (altText) fd.append("alt_text", altText);
        await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await reload();
    } finally {
      setUploading(false);
      setPendingUpload(null);
      setPendingAlt("");
    }
  };

  const confirmPendingUpload = () => {
    if (!pendingUpload) return;
    runUpload(pendingUpload.files, pendingUpload.folder, pendingAlt.trim());
  };

  const cancelPendingUpload = () => {
    setPendingUpload(null);
    setPendingAlt("");
  };

  const onDelete = async (mid) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/admin/media/${mid}`);
    reload();
  };

  const toggleFeaturedHome = async (mid, next) => {
    // Optimistic update so the toggle feels instant
    setItems((cur) => cur.map((m) => (m.media_id === mid ? { ...m, featured_home: next } : m)));
    try {
      await api.patch(`/admin/media/${mid}`, { featured_home: next });
    } catch (e) {
      setItems((cur) => cur.map((m) => (m.media_id === mid ? { ...m, featured_home: !next } : m)));
      window.alert(e?.response?.data?.detail || "Failed to update");
    }
  };

  const saveAlt = async (mid) => {
    const next = altDraft.trim();
    setItems((cur) => cur.map((m) => (m.media_id === mid ? { ...m, alt_text: next } : m)));
    setEditingAlt(null);
    try {
      await api.patch(`/admin/media/${mid}`, { alt_text: next });
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Failed to save alt text");
      reload();
    }
  };

  const copyUrl = (mid) => {
    const url = mediaUrl(mid);
    navigator.clipboard?.writeText(url);
    setCopied(mid);
    setTimeout(() => setCopied(""), 1500);
  };

  const createFolder = () => {
    const name = newFolderName.trim().toLowerCase().replace(/\s+/g, "-");
    if (!name) return;
    setActive(name);
    setNewFolderName("");
  };

  return (
    <div data-testid="admin-media-page">
      <header className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne mb-2">Library</p>
          <h1 className="font-serif text-3xl text-aubergine">Media</h1>
          <p className="mt-2 text-sm text-aubergine/65">Upload and organise logos, photos, and assets in folders.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="admin-media-upload-button" className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2.5 font-mono text-xs tracking-[0.04em] disabled:opacity-60">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Upload files
          </button>
          <input ref={inputRef} type="file" multiple onChange={onFilesPicked} className="hidden" />
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-4">
        <aside className="lg:col-span-1 space-y-4">
          <div className="rounded-lg border border-aubergine/10 bg-white p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-aubergine/55 mb-3">Folders</p>
            <button
              onClick={() => setActive(null)}
              data-testid="folder-all"
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm ${active === null ? "bg-champagne/15 text-aubergine font-medium" : "text-aubergine/65 hover:bg-aubergine/5"}`}
            >
              <Folder className="h-3.5 w-3.5" /> All media
              <span className="ml-auto text-[11px] text-aubergine/45">{!active ? items.length : ""}</span>
            </button>
            {folders.map((f) => (
              <button
                key={f}
                onClick={() => setActive(f)}
                data-testid={`folder-${f.replace(/\//g, "-")}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm ${active === f ? "bg-champagne/15 text-aubergine font-medium" : "text-aubergine/65 hover:bg-aubergine/5"}`}
              >
                <Folder className="h-3.5 w-3.5" /> {f}
              </button>
            ))}
            <div className="mt-3 pt-3 border-t border-aubergine/10">
              <div className="flex gap-1.5">
                <input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} data-testid="folder-new-input" placeholder="new-folder" className="w-full rounded-md border border-aubergine/15 bg-white px-2 py-1.5 text-xs outline-none focus:border-champagne" />
                <button onClick={createFolder} data-testid="folder-new-create" className="rounded-md border border-aubergine/15 hover:bg-aubergine/5 px-2 py-1.5"><FolderPlus className="h-3.5 w-3.5" /></button>
              </div>
              <p className="mt-1.5 text-[10px] text-aubergine/45">Create a folder by selecting it; next upload goes there.</p>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="mb-3 text-xs text-aubergine/55 font-mono uppercase tracking-wider">
            {active ? `📁 ${active}` : "All media"} · {items.length} item{items.length === 1 ? "" : "s"}
          </div>

          {loading ? (
            <div className="py-16 flex items-center justify-center text-aubergine/55"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-aubergine/15 p-12 text-center" data-testid="admin-media-empty">
              <ImageIcon className="mx-auto h-10 w-10 text-aubergine/30" />
              <p className="mt-3 text-sm text-aubergine/65">No files yet in this folder.</p>
              <button onClick={() => inputRef.current?.click()} className="mt-4 inline-flex items-center gap-2 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-4 py-2 font-mono text-xs">
                <Upload className="h-3.5 w-3.5" /> Upload your first file
              </button>
            </div>
          ) : (
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((m) => {
                const isImage = (m.content_type || "").startsWith("image/");
                const isPartner = m.folder === "partners";
                const isHome = !!m.featured_home;
                return (
                  <div key={m.media_id} className="group rounded-lg border border-aubergine/10 bg-white overflow-hidden" data-testid={`media-item-${m.media_id}`}>
                    <div className="relative aspect-square bg-bone/60 flex items-center justify-center">
                      {isImage ? (
                        <img src={mediaUrl(m.media_id)} alt={m.alt_text || m.original_filename} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-aubergine/40">
                          <ImageIcon className="h-8 w-8" />
                          <span className="font-mono text-[10px] uppercase">{(m.original_filename || "").split(".").pop()}</span>
                        </div>
                      )}
                      {isPartner && isHome && (
                        <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-champagne text-aubergine font-mono text-[9px] uppercase tracking-wider px-2 py-0.5">
                          <Star className="h-2.5 w-2.5 fill-current" /> Home
                        </span>
                      )}
                    </div>
                    <div className="p-2.5">
                      <div className="text-xs text-aubergine truncate font-medium" title={m.original_filename}>{m.original_filename}</div>
                      <div className="text-[10px] text-aubergine/55 font-mono uppercase tracking-wider truncate">{m.folder}</div>
                      {isImage && (
                        editingAlt === m.media_id ? (
                          <div className="mt-2">
                            <input
                              value={altDraft}
                              onChange={(e) => setAltDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveAlt(m.media_id);
                                if (e.key === "Escape") { setEditingAlt(null); setAltDraft(""); }
                              }}
                              autoFocus
                              placeholder="Describe this image…"
                              data-testid={`media-alt-input-${m.media_id}`}
                              className="w-full rounded border border-aubergine/15 px-2 py-1 text-[11px] outline-none focus:border-champagne"
                            />
                            <div className="mt-1 flex items-center justify-between text-[10px]">
                              <button onClick={() => saveAlt(m.media_id)} data-testid={`media-alt-save-${m.media_id}`} className="text-champagne hover:underline font-mono uppercase tracking-wider">Save</button>
                              <button onClick={() => { setEditingAlt(null); setAltDraft(""); }} className="text-aubergine/55 hover:text-aubergine font-mono uppercase tracking-wider">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => { setEditingAlt(m.media_id); setAltDraft(m.alt_text || ""); }}
                            data-testid={`media-alt-edit-${m.media_id}`}
                            className={`mt-2 w-full text-left text-[11px] rounded border px-2 py-1 transition-colors ${
                              (m.alt_text || "").trim()
                                ? "border-aubergine/10 text-aubergine/75 hover:border-champagne/50"
                                : "border-signal-red/30 bg-signal-red/5 text-signal-red hover:border-signal-red/60"
                            }`}
                            title={(m.alt_text || "").trim() ? "Edit alt text" : "Missing alt text. Click to add"}
                          >
                            {(m.alt_text || "").trim() ? (
                              <span className="line-clamp-1"><span className="font-mono text-[9px] uppercase tracking-wider text-aubergine/45 mr-1">Alt</span>{m.alt_text}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1"><AlertTriangle className="h-2.5 w-2.5" /> Add alt text</span>
                            )}
                          </button>
                        )
                      )}
                      {isPartner && (
                        <label className="mt-2 flex items-center gap-1.5 text-[11px] text-aubergine/75 cursor-pointer select-none" data-testid={`media-featured-home-toggle-${m.media_id}`}>
                          <input
                            type="checkbox"
                            checked={isHome}
                            onChange={(e) => toggleFeaturedHome(m.media_id, e.target.checked)}
                            className="accent-champagne"
                          />
                          <span>Show on Home</span>
                        </label>
                      )}
                      <div className="mt-2 flex items-center gap-1.5">
                        <button onClick={() => copyUrl(m.media_id)} data-testid={`media-copy-${m.media_id}`} className="flex-1 inline-flex items-center justify-center gap-1 rounded border border-aubergine/15 hover:bg-aubergine/5 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-aubergine/65">
                          {copied === m.media_id ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> URL</>}
                        </button>
                        <button onClick={() => onDelete(m.media_id)} data-testid={`media-delete-${m.media_id}`} className="rounded border border-aubergine/15 hover:bg-signal-red/5 hover:border-signal-red/40 px-2 py-1 text-aubergine/55 hover:text-signal-red">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {pendingUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-aubergine/40 backdrop-blur-sm" data-testid="media-alt-prompt">
          <div className="w-full max-w-md rounded-lg bg-white border border-aubergine/15 shadow-xl overflow-hidden">
            <header className="px-5 py-3 border-b border-aubergine/10 bg-bone/60">
              <div className="font-serif text-lg text-aubergine">Add <em className="accent-italic">alt text</em></div>
              <div className="text-[11px] font-mono text-aubergine/55 mt-0.5">
                {pendingUpload.files.length} image{pendingUpload.files.length === 1 ? "" : "s"} · folder: {pendingUpload.folder}
              </div>
            </header>
            <div className="p-5 space-y-3">
              <p className="text-xs text-aubergine/70">
                Alt text describes an image for screen readers and search engines. Keep it short and specific.
              </p>
              <textarea
                value={pendingAlt}
                onChange={(e) => setPendingAlt(e.target.value)}
                rows={3}
                placeholder="e.g. T-REX Network ledger diagram showing tokenized flows across three jurisdictions"
                data-testid="media-alt-prompt-input"
                className="w-full rounded-md border border-aubergine/15 bg-white px-3 py-2 text-sm outline-none focus:border-champagne resize-none"
              />
              <div className="text-[10px] font-mono text-aubergine/45">
                {pendingUpload.files.length > 1 ? "This text will be applied to every image in the batch. You can edit each one afterwards." : "You can edit this later from the image card."}
              </div>
            </div>
            <footer className="flex items-center justify-end gap-3 px-5 py-3 border-t border-aubergine/10 bg-bone/40">
              <button
                onClick={confirmPendingUpload}
                disabled={uploading}
                data-testid="media-alt-prompt-skip"
                className="text-aubergine/55 hover:text-aubergine font-mono text-[11px] uppercase tracking-wider"
                title="Upload without alt text (you can add it later)"
              >
                Skip for now
              </button>
              <button onClick={cancelPendingUpload} data-testid="media-alt-prompt-cancel" className="rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-2 font-mono text-xs text-aubergine">
                Cancel
              </button>
              <button
                onClick={confirmPendingUpload}
                disabled={uploading || !pendingAlt.trim()}
                data-testid="media-alt-prompt-upload"
                className="inline-flex items-center gap-2 rounded-md bg-aubergine text-bone hover:bg-aubergine/90 px-4 py-2 font-mono text-xs disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Upload
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
